import {
  LIVEABOARD_FACILITIES,
  DISCOUNT_METHODS,
  DIVE_CENTER_FACILITIES,
} from "../helpers/constants";
import { mapState, mapGetters } from "vuex";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import currencyRanges from "../helpers/currencyRanges.json";

const DISPLAY_LIVEABOARD_FACILITIES = ["internet", "nitrox"];

export default {
  computed: {
    ...mapGetters([
      "selectedMonth",
      "getValidTrips",
      "getTripsForSelectedMonth",
    ]),
    ...mapState(["filters"]),
    tripadvisorImage() {
      const rating = this.resort.__platform_rating.toFixed(1);

      return `https://www.tripadvisor.com/img/cdsi/img2/ratings/traveler/${rating}-MCID-5.svg`;
    },
    displayingFacilities() {
      let facilities = get(this, ["resort", "inclusions_objects"], []);
      facilities = facilities.filter(
        (item) =>
          (item.list_id === LIVEABOARD_FACILITIES ||
            item.list_id === DIVE_CENTER_FACILITIES) &&
          DISPLAY_LIVEABOARD_FACILITIES.includes(item.slug)
      );
      facilities = orderBy(facilities, ["order", "name"], ["asc", "asc"]);

      return facilities.reverse();
    },
    validTrips() {
      return this.getValidTrips(this.resort.__odyssey_trips);
    },
    nextAvailableMonth() {
      const currentMonth = moment().startOf("month");

      const aTrip = this.validTrips.find((trip) => {
        const mmTrip = moment(trip.date, "YYYY-MM-DD").startOf("month");

        return mmTrip.isSameOrAfter(currentMonth, "month");
      });

      if (aTrip) {
        return moment(aTrip.date, "YYYY-MM-DD");
      }

      return null;
    },
    monthToShow() {
      let monthToShow = moment(
        get(window, ["topNavStore", "state", "formData", "dates", "0"], null)
      );
      if (monthToShow.isValid() === false) {
        monthToShow = this.nextAvailableMonth;
      }

      return monthToShow;
    },
    guests() {
      const guests = get(
        window,
        ["topNavStore", "state", "formData", "guests"],
        null
      );

      if (guests === null) {
        return "0";
      }

      return guests;
    },
    tripsForSelectedMonthAndFilters() {
      const trips = this.getTripsForSelectedMonth(this.validTrips);
      const budgets = get(this.filters, ["budgets"], "")
        .split(",")
        .filter((f) => f);
      const numDives = get(this.filters, ["numDives"], "")
        .split(",")
        .filter((f) => f);

      return trips
        .filter((trip) => {
          if (budgets.length === 0) {
            return true;
          }

          const price = get(trip, ["price", "price"], 0);
          const currency = window.topNavStore.state.currency;
          const rate = window.zubluCurrencyTable[currency.code];
          const ranges = budgets.map((option) => {
            const minValue = Number(option);
            const budgetByCurrency =
              currencyRanges[currency.code].liveaboard.filterRange[minValue];

            return [budgetByCurrency[0] * rate, budgetByCurrency[1] * rate];
          });

          return ranges.some((range) => {
            if (price >= range[0] && price < range[1]) {
              return true;
            }

            return false;
          });
        })
        .filter((trip) => {
          if (numDives.length === 0) {
            return true;
          }

          if (numDives.includes("5") && [1, 2].includes(trip.experience_id)) {
            return true;
          }
          if (
            numDives.includes("31") &&
            [1, 2, 3, 4].includes(trip.experience_id)
          ) {
            return true;
          }
          if (
            numDives.includes("51") &&
            [1, 2, 3, 4, 6, 7].includes(trip.experience_id)
          ) {
            return true;
          }
          if (
            numDives.includes("101") &&
            [1, 2, 3, 4, 5, 6, 7].includes(trip.experience_id)
          ) {
            return true;
          }

          return false;
        });
    },
    supplierURL() {
      const from = this.monthToShow
        ? this.monthToShow.startOf("month").format("YYYY-MM-DD")
        : "";

      let query = from ? `?from=${from}` : "";

      const guests = this.guests;
      if (guests !== "0") {
        query = query + `&guests=${guests}`;
      }

      return `${this.resort.__platform_url}${query}`;
    },
  },
  filters: {
    tripDate(date) {
      return moment(date, "YYYY-MM-DD").format("DD MMM YYYY");
    },
    formatAvailability(availability) {
      switch (availability) {
        case "Available":
          return "Available";
        case "Sold out":
          return "Sold out";
        case "Limited availability":
          return "Limited";
        case "Available for charter":
          return "Charter only";
        case "2S spaces":
          return "2 shared spaces";
        case "1F space":
          return "1 female space";
        case "1M space":
          return "1 male space";
        default:
          return availability;
      }
    },
    formatPrice(price) {
      return price.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    },
  },
  methods: {
    hasDiscount(trip) {
      if (
        trip.discount_method === DISCOUNT_METHODS.FIX_AMOUNT &&
        trip.discount_price
      ) {
        return true;
      }

      if (
        trip.discount_method === DISCOUNT_METHODS.PERCENTAGE &&
        trip.discount_price
      ) {
        return true;
      }

      if (trip.discount_method === DISCOUNT_METHODS.CUSTOM) {
        return true;
      }

      return false;
    },
    getTripClass(trip) {
      let base = [];

      if (trip.availability === "Available") {
        base = ["trip-status-available"];
      } else if (trip.availability === "Sold out") {
        base = ["trip-status-sold-out"];
      } else if (
        [
          "Limited availability",
          "4 spaces",
          "3 spaces",
          "2 spaces",
          "2S spaces",
          "1 space",
          "1F space",
          "1M space",
        ].includes(trip.availability)
      ) {
        base = ["trip-status-limited-availability"];
      } else if (trip.availability === "Available for charter") {
        base = ["trip-status-charter-only"];
      } else if (trip.availability === "On request") {
        base = ["trip-status-on-request"];
      }

      if (moment(trip.date).diff(moment()) < 0) {
        base.push("trip-past-date");
      }

      if (this.hasDiscount(trip)) {
        base.push("trip-discounted");
      }

      return base;
    },
  },
};
